// DPoP (RFC 9449) proof-of-possession, per ADR-002.
//
// - Client generates a NON-EXTRACTABLE ES256 keypair in WebCrypto. The private
//   key never leaves the device and cannot be exported (extractable:false).
// - The access/refresh tokens are bound to this key's JWK thumbprint (`jkt`),
//   which the server stores. A stolen bearer token is useless without the key.
// - On every API call we attach a fresh DPoP proof JWT signed by the private key:
//   header {typ:"dpop+jwt", alg:"ES256", jwk:<public JWK>}, claims
//   {htm, htu, jti, iat, nonce?, ath?}.
//
// The keypair is non-extractable (private key never exportable). To survive a
// page reload WITHOUT weakening security, we persist the live CryptoKeyPair
// OBJECTS in IndexedDB: structured clone stores non-extractable CryptoKeys with
// the private key material still sealed (it cannot be read back as JWK/raw).
// On reload we reload the SAME key (same jkt), so the DPoP-bound refresh token
// still verifies against the server-pinned jkt and the session survives.
// If IndexedDB is unavailable (private mode, etc.) we fall back to an ephemeral
// in-memory key (previous behavior) — reload then requires re-login.

import { b64urlEncode, utf8 } from './base64.js';

export interface DPoPKeypair {
  publicJwk: JsonWebKey;
  jkt: string; // RFC 7638 JWK thumbprint (base64url SHA-256)
  sign(data: Uint8Array): Promise<Uint8Array>;
}

let current: DPoPKeypair | null = null;

// --- IndexedDB persistence for the non-extractable CryptoKeyPair -------------
const IDB_NAME = 'vid-dpop';
const IDB_STORE = 'keys';
const IDB_KEY = 'current';

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('no-indexeddb')); return; }
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb-open-failed'));
  });
}

function idbReq<T>(store: IDBObjectStore, r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('idb-req-failed'));
    void store; // tx completion tracked via request events
  });
}

async function idbStoreKeypair(kp: CryptoKeyPair): Promise<void> {
  try {
    const db = await idbOpen();
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      // Structured clone stores the live CryptoKey objects (private key sealed).
      await idbReq(tx.objectStore(IDB_STORE), tx.objectStore(IDB_STORE).put(kp as unknown as object, IDB_KEY));
    } finally { db.close(); }
  } catch { /* IndexedDB unavailable — stay in-memory only */ }
}

async function idbLoadKeypair(): Promise<CryptoKeyPair | null> {
  try {
    const db = await idbOpen();
    try {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const val = await idbReq<unknown>(tx.objectStore(IDB_STORE), tx.objectStore(IDB_STORE).get(IDB_KEY));
      const kp = val as CryptoKeyPair | undefined;
      if (kp && kp.privateKey && kp.publicKey) return kp;
      return null;
    } finally { db.close(); }
  } catch { return null; }
}

async function idbClearKeypair(): Promise<void> {
  try {
    const db = await idbOpen();
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      await idbReq(tx.objectStore(IDB_STORE), tx.objectStore(IDB_STORE).delete(IDB_KEY));
    } finally { db.close(); }
  } catch { /* nothing to clear */ }
}

// Wrap a live CryptoKeyPair (freshly generated OR loaded from IndexedDB) into
// the DPoPKeypair interface: export the public JWK, compute jkt, bind sign().
async function wrapKeypair(kp: CryptoKeyPair): Promise<DPoPKeypair> {
  const publicJwk = await crypto.subtle.exportKey('jwk', kp.publicKey);
  delete (publicJwk as Record<string, unknown>).key_ops;
  delete (publicJwk as Record<string, unknown>).ext;
  const jkt = await jwkThumbprint(publicJwk);
  return {
    publicJwk: { kty: publicJwk.kty, crv: publicJwk.crv, x: publicJwk.x, y: publicJwk.y },
    jkt,
    async sign(data: Uint8Array): Promise<Uint8Array> {
      const sig = await crypto.subtle.sign(
        { name: 'ECDSA', hash: 'SHA-256' },
        kp.privateKey,
        data as unknown as ArrayBuffer,
      );
      return new Uint8Array(sig);
    },
  };
}

async function jwkThumbprint(jwk: JsonWebKey): Promise<string> {
  // RFC 7638: lexicographically-ordered required members, no whitespace.
  const canonical = JSON.stringify({ crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y });
  const digest = await crypto.subtle.digest('SHA-256', utf8(canonical) as BufferSource);
  return b64urlEncode(digest);
}

// Convert WebCrypto raw ECDSA signature (already r||s) — subtle already returns
// the JOSE-style concatenated R||S for P-256, so no DER unwrap needed.
export async function createDPoPKeypair(): Promise<DPoPKeypair> {
  const kp = (await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false, // extractable: false — private key non-exportable
    ['sign'],
  )) as CryptoKeyPair;

  const pair = await wrapKeypair(kp);
  current = pair;
  // Best-effort persist the live keypair so a reload reuses the SAME jkt.
  await idbStoreKeypair(kp);
  return pair;
}

export function getDPoPKeypair(): DPoPKeypair | null {
  return current;
}

// Load the persisted keypair (if any) into `current` without generating a new one.
async function loadPersistedKeypair(): Promise<DPoPKeypair | null> {
  const kp = await idbLoadKeypair();
  if (!kp) return null;
  try {
    const pair = await wrapKeypair(kp);
    current = pair;
    return pair;
  } catch { return null; }
}

export async function ensureDPoPKeypair(): Promise<DPoPKeypair> {
  if (current) return current;
  // On reload `current` is null: try to restore the persisted (bound) key first,
  // so the DPoP-bound refresh token still verifies against the server jkt.
  const restored = await loadPersistedKeypair();
  if (restored) return restored;
  return createDPoPKeypair();
}

export function resetDPoPKeypair(): void {
  current = null;
  // Full rotation on explicit logout: drop the persisted key too so the next
  // login mints a brand-new jkt. (Fire-and-forget; safe if IDB is unavailable.)
  void idbClearKeypair();
}

/**
 * Build a DPoP proof JWT for a specific request.
 * @param htm HTTP method (upper-case)
 * @param htu HTTP target URI (no query/fragment per RFC)
 * @param opts.nonce server-issued nonce (from `DPoP-Nonce` response header)
 * @param opts.accessToken when present, adds `ath` (base64url SHA-256 of the token)
 */
export async function dpopProof(
  htm: string,
  htu: string,
  opts: { nonce?: string; accessToken?: string } = {},
): Promise<string> {
  const kp = await ensureDPoPKeypair();
  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk: kp.publicJwk,
  };
  const url = new URL(htu);
  const claims: Record<string, unknown> = {
    htm: htm.toUpperCase(),
    htu: `${url.origin}${url.pathname}`,
    jti: crypto.randomUUID(),
    iat: Math.floor(Date.now() / 1000),
  };
  if (opts.nonce) claims.nonce = opts.nonce;
  if (opts.accessToken) {
    const ath = await crypto.subtle.digest('SHA-256', utf8(opts.accessToken) as BufferSource);
    claims.ath = b64urlEncode(ath);
  }
  const signingInput = `${b64urlEncode(utf8(JSON.stringify(header)))}.${b64urlEncode(utf8(JSON.stringify(claims)))}`;
  const sig = await kp.sign(utf8(signingInput));
  return `${signingInput}.${b64urlEncode(sig)}`;
}
